package dev.akshaychordiya.pages.articles

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initMagicalBookOfComposePerformanceDeferStateReadsPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("articles/MagicalBookOfComposePerformanceDeferStateReads.md", FrontMatterElement.Builder { addScalar("title", "Magical Book of Compose Performance — Defer State Reads✨"); addScalar("date", "2026-06-26"); addScalar("description", "We recently gave a talk 🎤 at a couple of Android conferences about how to debug Compose Compose performance & various tools in your arsenal 🛠🧰"); addScalar("originalUrl", "https://proandroiddev.com/magical-book-of-compose-performance-defer-state-reads-f15486bee807"); addScalar("originalName", "Medium"); addScalar("image", "/images/articles/magical-book-of-compose-performance-defer-state-reads/1z3u6zaLjtFsIcVk_GekF1Q.webp"); }))
}

@Page("/articles/magical-book-of-compose-performance-defer-state-reads")
@Layout(".components.layouts.ArticleLayout")
@Composable
fun MagicalBookOfComposePerformanceDeferStateReadsPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/magical-book-of-compose-performance-defer-state-reads/1z3u6zaLjtFsIcVk_GekF1Q.webp", "")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Person reading the Compose Performance Book (Credits ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://medium.com/u/4b8e1a522d2e") {
                org.jetbrains.compose.web.dom.Text("Tasha Ramesh")
            }
            org.jetbrains.compose.web.dom.Text(")")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("magical-book-of-compose-performance-defer-state-reads") }) {
        org.jetbrains.compose.web.dom.Text("📚 Magical Book of Compose Performance — Defer State Reads✨")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We recently gave a talk 🎤 at a couple of Android conferences about how to debug Compose Compose performance & various tools in your arsenal 🛠🧰")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To make it fun we had this quirky idea where we presented the idea of this ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Magical Book of Compose performance")
        }
        org.jetbrains.compose.web.dom.Text(" being our source and after the talk, a surprising number of people came up to us asking: ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("“Wait, is that a real book ? Where can I buy it?”")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It wasn’t. It was a prop. But the reaction + the number of people who pulled me aside after each talk saying “you should write this up”; gave me an idea to cover this in an article form.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("If you want to see the full talk first, it’s")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtu.be/1tAUmuTXltE?si=HRdYv1z3Z4yTnQJK") {
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("here on YouTube")
            }
        }
        org.jetbrains.compose.web.dom.Text("  📺")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-quick-refresher-compose-s-three-phases") }) {
        org.jetbrains.compose.web.dom.Text("📜 A Quick Refresher: Compose’s Three Phases")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Before we get into deferring state reads, you need to understand how Compose renders a frame. It does so in three phases:")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/magical-book-of-compose-performance-defer-state-reads/0zjqnWe01ozFaRPSW.webp", "")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Composition — Compose runs your @Composable functions and builds the UI tree.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Layout — Compose measures and places each node on screen.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Drawing — Compose actually draws the pixels.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The key insight for performance is this ⚡:")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("The fewer phases Compose has to run, the better your performance.")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That means if a state change only affects ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("where")
        }
        org.jetbrains.compose.web.dom.Text(" something is drawn (e.g. an offset during a drag), ideally Compose should skip Composition and Layout entirely and jump straight to Drawing. Whether or not that happens depends on ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("where")
        }
        org.jetbrains.compose.web.dom.Text(" you read that state.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("This is where things get interesting:")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If a state value is read during ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Composition")
        }
        org.jetbrains.compose.web.dom.Text(", a change triggers recomposition — all three phases run again for that subtree. If it’s read during ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Layout")
        }
        org.jetbrains.compose.web.dom.Text(", only the layout phase re-runs. If it’s read during ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Drawing")
        }
        org.jetbrains.compose.web.dom.Text(", only the draw commands re-execute.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The further ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("down")
        }
        org.jetbrains.compose.web.dom.Text(" the pipeline you can push a state read, the less work Compose has to do when that state changes frequently — like during a drag gesture, where state updates can arrive every single frame.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-problem-reading-state-too-early") }) {
        org.jetbrains.compose.web.dom.Text("⚠️ The Problem: Reading State Too Early")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Imagine a swipeable card. As the user drags it, you track the offset with a State<Float>. You might naturally write something like this:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@Composable\nfun SwipeableCard(state: SwipeableCardState) {\n    Card(\n        modifier = Modifier\n            .offset(\n                x = state.offset.x.dp,\n                y = state.offset.y.dp\n            )\n            .graphicsLayer(\n                scaleX = state.scaleFactor,\n                scaleY = state.scaleFactor\n            )\n    )\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This looks fine. But there’s a subtle performance problem here.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When state.offset changes which happens on ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("every single frame")
        }
        org.jetbrains.compose.web.dom.Text(" while the user is dragging, Compose reads that state inside the SwipeableCard composable body. This means Compose marks the entire SwipeableCard scope for recomposition. It re-runs the composable, re-evaluates the modifier chain, goes through Composition, Layout, and Drawing — all for something that only affects where and how the card is drawn.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You’re paying 💸 for three phases when you only need one.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-fix-lambda-versions-of-modifiers") }) {
        org.jetbrains.compose.web.dom.Text("💡The Fix: Lambda Versions of Modifiers")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both Modifier.offset and Modifier.graphicsLayer have lambda variants:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@Composable\nfun SwipeableCard(state: SwipeableCardState) {\n    Card(\n        modifier = Modifier\n            .offset {\n                IntOffset(\n                    x = state.offset.x.roundToInt(),\n                    y = state.offset.y.roundToInt()\n                )\n            }\n            .graphicsLayer {\n                scaleX = state.scaleFactor\n                scaleY = state.scaleFactor\n            }\n    )\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The difference is subtle but significant. The lambda versions defer the state read. Compose doesn’t read state.offset during Composition — it reads it later, during the Layout or Drawing phase, inside the lambda.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This means when the offset changes:")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("✅ Compose skips Composition entirely")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Text("✅ Compose skips Layout (for graphicsLayer)")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Text("✅ Only Drawing runs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For a card being dragged around the screen at 60–120fps, this is a meaningful win.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-does-this-work") }) {
        org.jetbrains.compose.web.dom.Text("Why Does This Work?")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Compose tracks state reads per scope. When you read a state value, Compose registers the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("current scope")
        }
        org.jetbrains.compose.web.dom.Text(" as an observer of that state. When the state changes, only the registered scope re-executes.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Lambda modifiers create their own tight scope, just the lambda body. So when the offset state changes, only that lambda re-runs, not the whole composable tree above it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("going-further-defer-state-reads-down-your-composable-tree") }) {
        org.jetbrains.compose.web.dom.Text("Going Further: Defer State Reads Down Your Composable Tree 🌳")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The same principle applies beyond just modifier lambdas. Consider this pattern:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@Composable\nfun CardStack(state: CardStackState) {\n    SwipeableCard(\n        // state read here, so recomposition scope is CardStack\n        scaleFactor = if (state.cards.size > 1) { \n            calculateScale(state.offset) \n        } else 1f,\n        canStartDragging = state.cards.isNotEmpty()\n    )\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Here, state.offset and state.cards are read inside CardStack. This means CardStack recomposes every time the drag offset changes — and so does everything inside it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("💡 The fix is to pass ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("lambdas")
        }
        org.jetbrains.compose.web.dom.Text(" instead of computed values, so the read is deferred until the child composable actually needs it:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@Composable\nfun CardStack(state: CardStackState) {\n    SwipeableCard(\n        scaleFactor = { calculateScale(state.offset) },\n        canStartDragging = { state.cards.isNotEmpty() }\n    )\n}\n") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@Composable\nfun SwipeableCard(\n    scaleFactor: () -> Float,\n    canStartDragging: () -> Boolean\n) {\n    Card(\n        modifier = Modifier.graphicsLayer {\n            scaleX = scaleFactor() // state read happens here, in Drawing phase\n            scaleY = scaleFactor()\n        }\n    )\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now the state read happens inside SwipeableCard, deep in the modifier lambda. CardStack is no longer an observer of state.offset — so it doesn't recompose when the card is being dragged.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The recomposition scope is smaller. The phases executed are fewer. The performance is better ⬆︎")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-mental-model-to-keep") }) {
        org.jetbrains.compose.web.dom.Text("🧠 The Mental Model to Keep")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When you’re writing Compose code and you read a State value, ask yourself:")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Does this value change frequently? And does reading it here force more of the tree to recompose than actually needs to?")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If yes → defer it. Push the read as far down the tree as possible, ideally into a modifier lambda or a tight composable scope that only cares about that one value.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The rule of thumb 👍")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("✅ Use Modifier.offset { } and Modifier.graphicsLayer { } (lambda versions) for anything that changes on every frame.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("✅ Pass lambdas instead of computed values when the parent shouldn’t own the state observation.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("✅ Read state as close to where it’s ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("used")
        }
        org.jetbrains.compose.web.dom.Text(" as possible, not where it’s convenient.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-to-verify-this-is-working") }) {
        org.jetbrains.compose.web.dom.Text("🔍 How to verify this is working")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("You should see significant drop in recompositions in your layout inspector")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("You can setup Macrobenchmark 🧮 tests with FrameTimingMetric — a metric that tracks how many milliseconds frames are overshooting their deadline. The higher the frameOverrunMs, the jankier the experience")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Here’s the benchmark test I used to validate and measure the performance improvements:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("class JankBenchmark {\n    @get:Rule\n    val benchmarkRule = MacrobenchmarkRule()\n    \n    @Test\n    fun dragCardAround() = benchmarkRule.measureRepeated(\n        packageName = PackageName,\n        // Tracks how many milliseconds frames are overshooting their deadline\n        metrics = listOf(FrameTimingMetric()),\n        iterations = 10,\n        setupBlock = {\n            pressHome()\n            startActivityAndWait()\n            device.wait(Until.hasObject(By.res(TopCardResId)), 5000)\n        },\n        measureBlock = {\n            // The interaction you want to measure\n            val card = device.findObject(By.res(TopCardResId))\n            val dragDistance = 210\n            val speed = 100\n            dragCardAround(card, dragDistance, speed)\n        }\n    )\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("If you found this useful, the original talk this was based on was presented at Droidcon SF, AndroidMakers and more alongside")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://twitter.com/TashaRamesh") {
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("@TashaRamesh")
            }
        }
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://proandroiddev.com/magical-book-of-compose-performance-defer-state-reads-f15486bee807") {
            org.jetbrains.compose.web.dom.Text("📚 Magical Book of Compose Performance — Defer State Reads✨")
        }
        org.jetbrains.compose.web.dom.Text(" was originally published in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://proandroiddev.com") {
            org.jetbrains.compose.web.dom.Text("ProAndroidDev")
        }
        org.jetbrains.compose.web.dom.Text(" on Medium, where people are continuing the conversation by highlighting and responding to this story.")
    }
}
